import { useMerchant } from "@auth";
import { Button, FileInput, Modal } from "@components";
import { useEffect, useRef, useState } from "react";
import { Download, Upload } from "react-bootstrap-icons";
import { CSVDownload } from "react-csv";
import { arrayMove } from "@dnd-kit/sortable";
import { useColumnManager, TableMenu, TableMenuItem } from "./shared";
import TableCore from "./shared/TableCore";

const ServerTable = ({
  config,
  data,
  loading = false,
  statistics: rawStatistics,
  results,
  page,
  pageSize,
  setPage,
  filterManager,
  sort,
  sortDirection,
  onSort,
  searchTerm,
  onSearchTermChange,
  exportData,
  importData,
  selectedData = [],
  allSelected = false,
  deselectedIds = new Set(),
  onSelectAllChanged,
  onSelectedChange,
  toolbarLeft,
  selectedItemLabel = "selected",
  headerAction,
  isRowHighlighted,
}) => {
  const {
    filters,
    setFilters,
    addFilter,
    updateFilter,
    removeFilter,
    autoOpenKey,
    newFilterButtonRef,
    getColTitle,
    scope,
  } = filterManager;

  const { merchant, hasPermission } = useMerchant();
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [page]);

  const shouldShowCol = (x) => {
    return (
      !x.hide &&
      (!x.permission || hasPermission(x.permission)) &&
      !(merchant?.customisationSettings?.hiddenColumnKeys ?? []).includes(
        `${config.key}.${x.key}`,
      )
    );
  };

  const [loadedExportData, setLoadedExportData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [showImporter, setShowImporter] = useState(false);

  const onExport = async () => {
    setLoadedExportData(null);
    setExporting(true);
    try {
      const data = await exportData();
      setLoadedExportData(data);
    } finally {
      setExporting(false);
    }
  };

  const fileInputRef = useRef(null);

  const onImport = async () => {
    if (!importData || !importFile) return;
    setImporting(true);
    try {
      await importData(importFile);
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setShowImporter(false);
    } finally {
      setImporting(false);
    }
  };

  const {
    columnPreferences,
    setColumnPreferences,
    managedColumns,
    resetColumns,
  } = useColumnManager(
    config.columns.filter((x) => shouldShowCol(x)),
    config.key,
  );

  const handleColumnReorder = (activeId, overId) => {
    setColumnPreferences((prev) => {
      const oldIndex = prev.findIndex((p) => p.id === activeId);
      const newIndex = prev.findIndex((p) => p.id === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex).map((item, idx) => ({
        ...item,
        position: idx,
      }));
    });
  };

  // `key` doubles as the filter/orderBy parameter name, which for some columns differs from the
  // field the value is stored under on the row. Those columns declare `exportKey`; without it the
  // CSV column comes out present but empty.
  const headers = () =>
    managedColumns
      .filter((x) => !!x.key)
      .map((x) => ({ label: getColTitle(x), key: x.exportKey ?? x.key }));

  const statistics = rawStatistics
    ? rawStatistics.map((stat) => ({
        label: stat.label,
        value: stat.value,
        subStatistics: stat.subStatistics?.map((sub) => ({
          label: sub.label,
          value: sub.value,
        })),
      }))
    : null;

  const selectedCount = allSelected
    ? results - deselectedIds.size
    : selectedData.length;

  const primaryBulkAction = (config.bulkActions || []).find(
    (a) => a.primary && (!a.permission || hasPermission(a.permission)),
  );

  const PrimaryBulkActionIcon = primaryBulkAction?.icon;

  const primaryHeaderAction = primaryBulkAction ? (
    <>
      <span className="text-sm font-semibold whitespace-nowrap text-zinc-700">
        {selectedCount}{" "}
        {typeof selectedItemLabel === "function"
          ? selectedItemLabel(selectedCount)
          : selectedItemLabel}
      </span>
      <Button
        color={primaryBulkAction.color ?? "primary"}
        disabled={selectedCount < (primaryBulkAction.minSelected ?? 1)}
        onClick={() => primaryBulkAction.action(selectedData)}
        className="whitespace-nowrap"
      >
        {PrimaryBulkActionIcon && (
          <PrimaryBulkActionIcon aria-hidden="true" className="shrink-0 inline mr-2 size-4" />
        )}
        {primaryBulkAction.label}
      </Button>
    </>
  ) : null;

  const visibleMenuItems = (config.menu || []).filter(
    (item) =>
      !item.hidden && (!item.permission || hasPermission(item.permission)),
  );

  const menu = (
    <TableMenu
      columnsManager={{
        data: columnPreferences,
        setData: setColumnPreferences,
        onReset: resetColumns,
      }}
    >
      {visibleMenuItems.map((item, i) => (
        <TableMenuItem
          key={`menu-${i}`}
          icon={item.icon}
          subtext={item.subtext}
          disabled={item.disabled}
          onClick={() => !item.disabled && item.onClick?.()}
        >
          {item.label}
        </TableMenuItem>
      ))}
      {exportData && (
        <TableMenuItem icon={Download} disabled={exporting} onClick={onExport}>
          Export
        </TableMenuItem>
      )}
      {importData && (
        <TableMenuItem icon={Upload} onClick={() => setShowImporter(true)}>
          Import
        </TableMenuItem>
      )}
    </TableMenu>
  );

  return (
    <>
      <TableCore
        config={config}
        rows={data}
        rawData={data}
        managedColumns={managedColumns}
        filters={filters}
        setFilters={setFilters}
        addFilter={addFilter}
        updateFilter={updateFilter}
        removeFilter={removeFilter}
        autoOpenKey={autoOpenKey}
        newFilterButtonRef={newFilterButtonRef}
        getColTitle={getColTitle}
        scope={scope}
        sort={sort}
        sortDirection={sortDirection}
        onSort={onSort}
        searchTerm={searchTerm}
        onSearchTermChange={onSearchTermChange}
        isAllChecked={allSelected}
        isRowChecked={(row) =>
          allSelected
            ? !deselectedIds.has(row.id)
            : selectedData.some((y) => y.id === row.id)
        }
        isRowHighlighted={isRowHighlighted}
        onSelectAllChanged={onSelectAllChanged}
        onSelectedChange={onSelectedChange}
        statistics={statistics}
        pagination={{
          onPrev: () => setPage(page - 1),
          onNext: () => setPage(page + 1),
          prevDisabled: page === 1,
          nextDisabled: results < page * pageSize + 1,
          label: `${(page - 1) * pageSize + 1} - ${Math.min(
            (page - 1) * pageSize + pageSize,
            results,
          )} of ${results} result${results > 1 ? "s" : ""}`,
        }}
        toolbarLeft={toolbarLeft}
        headerAction={headerAction}
        primaryHeaderAction={primaryHeaderAction}
        menu={menu}
        tableContainerRef={tableContainerRef}
        loading={loading}
        onColumnReorder={handleColumnReorder}
      />
      {loadedExportData && (
        <CSVDownload
          data={loadedExportData}
          headers={headers()}
          filename={`${config.key}_${new Date().getTime()}.csv`}
          target="_blank"
        />
      )}
      {importData && (
        <Modal
          isOpen={showImporter}
          onClose={() => {
            if (!importing) {
              setShowImporter(false);
              setImportFile(null);
            }
          }}
          title="Import file"
          size="sm"
        >
          <div className="p-5 flex flex-col gap-4">
            <FileInput
              name="erp-import-file"
              onChange={(files) => setImportFile(files?.[0] || null)}
            />
            <div className="flex justify-end gap-2">
              <Button
                color="secondary"
                onClick={() => {
                  setShowImporter(false);
                  setImportFile(null);
                }}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={onImport}
                disabled={!importFile || importing}
              >
                {importing ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ServerTable;
